import React, { createContext, useState, useEffect, useContext, ReactNode } from "react";

interface UserContextType {
  userRole: string | null;
  isLoading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const apiBaseUrl = process.env.GATSBY_API_URL;

  useEffect(() => {
    const fetchUserRole = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setUserRole(null);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${apiBaseUrl}/wp-json/wp/v2/users/me`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ action: "get_user_data" }),
        });

        if (response.ok) {
          const data = await response.json();
          setUserRole(data.roles?.[0] || "User");
        } else {
          console.error("Failed to fetch user data:", response.statusText);
          setUserRole(null);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setUserRole(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserRole();
  }, [apiBaseUrl]);

  return (
    <UserContext.Provider value={{ userRole, isLoading }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
